import React, { createContext, useCallback, useContext, useState } from "react";
import { Plane } from "lucide-react";

/**
 * BookNowFlyingLoader — full-screen navy overlay shown while
 * `/api/booking-clicks/outbound` is in flight (up to 2s while the
 * server resolves Booking's dest_id via autocomplete). Dismisses
 * the moment the click resolves, right before we open the new tab.
 *
 * Wiring:
 *   1. Mount `<BookNowFlyingLoaderMount />` once at the top of the
 *      React tree (App.js — done automatically by wrapping in
 *      `<BookNowLoaderProvider>`).
 *   2. Anywhere you call `bookNow({ card })`, pass the loader
 *      controller as `onLoadingChange`:
 *        const { setLoading } = useBookNowLoader();
 *        bookNow({ card, onLoadingChange: setLoading });
 *
 * The loader is purely presentational — z-50 fixed overlay, dismisses
 * on `setLoading(false)`. On cache hit (~4ms) it flickers imperceptibly;
 * on cache miss with autocomplete round-trip (~300-500ms) it holds long
 * enough to reassure the user without being annoying.
 */

const BookNowLoaderCtx = createContext({
  loading: false,
  setLoading: () => {},
});

export function useBookNowLoader() {
  return useContext(BookNowLoaderCtx);
}

export function BookNowLoaderProvider({ children }) {
  const [loading, setLoadingRaw] = useState(false);
  const [visible, setVisible] = useState(false);
  const showTimerRef = React.useRef(null);
  const setLoading = React.useCallback((v) => {
    setLoadingRaw(Boolean(v));
    // 2026-07-14 — debounced-show pattern: only render the loader if
    // the API call is still in flight after ~150ms. On cache hits
    // (~5ms) the click completes silently; slower paths (autocomplete
    // resolve ~300–500ms, or the 2-second sync ceiling) show the
    // plane. No forced minimum duration — dismiss immediately on
    // response.
    if (v) {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      showTimerRef.current = setTimeout(() => setVisible(true), 150);
    } else {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      setVisible(false);
    }
  }, []);
  React.useEffect(() => () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
  }, []);
  return (
    <BookNowLoaderCtx.Provider value={{ loading, setLoading }}>
      {children}
      <BookNowFlyingLoader visible={visible} />
    </BookNowLoaderCtx.Provider>
  );
}

export function BookNowFlyingLoader({ visible = false }) {
  if (!visible) return null;
  return (
    <div
      data-testid="booknow-flying-loader"
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6"
      style={{ backgroundColor: "#1a3a5f" }}
    >
      {/* Plane glyph — gold with gentle bobble. */}
      <div
        className="tb-plane-bobble"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 96,
          height: 96,
          borderRadius: "50%",
          background: "rgba(230,184,74,0.10)",
          border: "2px solid #e6b84a",
        }}
      >
        <Plane
          size={44}
          strokeWidth={1.75}
          color="#e6b84a"
          style={{ transform: "rotate(-20deg)" }}
        />
      </div>

      <div
        className="font-serif italic text-center px-6"
        style={{ color: "#f5f0e6", fontSize: "1.5rem", lineHeight: 1.3, maxWidth: 420 }}
      >
        Flying you to your destination now
      </div>

      {/* Cabin-style "FASTEN SEATBELT" sign. */}
      <div
        aria-hidden="true"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 18px",
          borderRadius: 999,
          backgroundColor: "#f5f0e6",
          color: "#1a3a5f",
          fontFamily: '"Outfit", system-ui, sans-serif',
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: "0.18em",
          boxShadow: "inset 0 0 0 1.5px #1a3a5f, 0 2px 0 rgba(0,0,0,0.15)",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: "#e6b84a",
            boxShadow: "0 0 6px #e6b84a",
          }}
        />
        FASTEN SEATBELT
      </div>

      {/* Local keyframes — no Tailwind config change needed. */}
      <style>{`
        @keyframes tb-plane-bobble {
          0%,100% { transform: translateY(0px); }
          50%     { transform: translateY(-4px); }
        }
        .tb-plane-bobble {
          animation: tb-plane-bobble 1.6s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .tb-plane-bobble { animation: none; }
        }
      `}</style>
    </div>
  );
}

export default BookNowFlyingLoader;
